import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { LOGIN } from '@/router/routes';
import { TAuthStep } from '@/types/auth';
import { clearStorageComponent, GetStorageComponent } from '@/utils/session';
import { clearPostLoginRedirect } from '@/utils/postLoginRedirect';

interface AuthContextProps {
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  step: TAuthStep;
  setStep: React.Dispatch<React.SetStateAction<TAuthStep>>;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  sessionToken: string;
  setSessionToken: React.Dispatch<React.SetStateAction<string>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  viewPassword: boolean;
  setViewPassword: React.Dispatch<React.SetStateAction<boolean>>;
  viewConfirmPassword: boolean;
  setViewConfirmPassword: React.Dispatch<React.SetStateAction<boolean>>;
  resetAuthFlow: () => void;
  logout: () => Promise<void>;
  hasAccessToken: () => boolean;
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!localStorage.getItem('accessToken'));

  // Restore Sentry user context from localStorage on app boot (page refresh while logged in)
  useEffect(() => {
    if (isAuthenticated) {
      try {
        const stored = GetStorageComponent('user');
        const user = stored ? JSON.parse(stored) : null;
        if (user) {
          Sentry.setUser({ id: String(user.id ?? ''), email: user.email, username: user.name });
        }
      } catch {
        // malformed storage value — ignore
      }
    }
  }, []);

  const wasAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      clearPostLoginRedirect();
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const [step, setStep] = useState<TAuthStep>('email');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [viewPassword, setViewPassword] = useState(false);
  const [sessionToken, setSessionToken] = useState<string>('');
  const [viewConfirmPassword, setViewConfirmPassword] = useState(false);

  const resetAuthFlow = () => {
    setStep('email');

    setEmail('');
    setSessionToken('');
    sessionStorage.removeItem('ats-active-sync-config-id');
    Object.keys(sessionStorage)
      .filter(key => key.startsWith('ats-sync-stream-'))
      .forEach(key => sessionStorage.removeItem(key));
    setError(null);
  };

  const hasAccessToken = () => {
    return Boolean(GetStorageComponent('accessToken'));
  };
  const logout = async () => {
    try {
      clearStorageComponent();
      resetAuthFlow();
      setIsAuthenticated(false);
      if (typeof window !== 'undefined' && window.location.pathname !== LOGIN) {
        window.location.replace(LOGIN);
      }
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const contextValue = {
    isAuthenticated,
    setIsAuthenticated,
    step,
    setStep,
    email,
    setEmail,
    sessionToken,
    setSessionToken,
    error,
    setError,
    viewConfirmPassword,
    viewPassword,
    setViewConfirmPassword,
    setViewPassword,
    resetAuthFlow,
    hasAccessToken,
    logout,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('AuthContext must be used within AuthProvider');
  }

  return context;
};
